const ENTRY_COLUMNS =
  "id,airport_code,player_name,score,landings,departures,pace,duration_seconds,started_at";
const MAX_ENTRIES = 200;
const MAX_RUN_ID_LENGTH = 128;
const MAX_PLAYER_NAME_LENGTH = 24;
const MAX_SCORE = 100_000;
const MAX_DURATION_SECONDS = 86_400;
/** Mirrors `SCORING.maxPointsPerFlight`, and the edge function's own copy. */
const MAX_POINTS_PER_FLIGHT = 600;

export class LeaderboardValidationError extends Error {
  readonly statusCode = 400;
  constructor(message = "Invalid leaderboard data.") {
    super(message);
    this.name = "LeaderboardValidationError";
  }
}

export class LeaderboardNotFoundError extends Error {
  readonly statusCode = 404;
  constructor() {
    super("Leaderboard entry not found.");
    this.name = "LeaderboardNotFoundError";
  }
}

export class LeaderboardUnavailableError extends Error {
  readonly statusCode: number;
  constructor({ rateLimited = false } = {}) {
    super("Board temporarily unavailable");
    this.name = "LeaderboardUnavailableError";
    this.statusCode = rateLimited ? 429 : 503;
  }
}

export interface LeaderboardRow {
  /** `uuid` in the schema, so a string — never a number. */
  id: string;
  airport_code?: string | null;
  player_name?: string | null;
  /** Absent on a row written before the column existed. */
  score?: number | string | null;
  landings: number | string;
  departures: number | string;
  pace: number | string;
  duration_seconds: number | string;
  started_at: string;
}

export interface LeaderboardEntry {
  id: string;
  airportCode: string;
  playerName: string | null;
  score: number;
  landings: number;
  departures: number;
  pace: number;
  durationSeconds: number;
  startedAt: string;
}

export interface ValidatedCompletedRun {
  runId: string;
  seed: number;
  score: number;
  landings: number;
  departures: number;
  durationSeconds: number;
  startedAt: string;
  pace: number;
}

export interface SupabaseQueryResult<T> {
  data?: T | null;
  error?: {
    status?: number;
    statusCode?: number;
    code?: string;
    message?: string;
  } | null;
}

export interface SupabaseClientLike {
  from(table: string): any;
}

function normalizeEnvironment(
  environment?: string,
): "development" | "production" {
  return environment === "development" ? "development" : "production";
}

function normalizeRunId(value: unknown): string {
  if (typeof value !== "string") {
    throw new LeaderboardValidationError();
  }

  const runId = value.trim();
  if (runId.length < 1 || runId.length > MAX_RUN_ID_LENGTH) {
    throw new LeaderboardValidationError();
  }
  return runId;
}

function isRateLimited(error: any): boolean {
  return (
    error?.status === 429 ||
    error?.statusCode === 429 ||
    String(error?.code ?? "") === "429" ||
    /rate.?limit|too many requests/i.test(String(error?.message ?? ""))
  );
}

function throwUnavailable(error?: any): never {
  throw new LeaderboardUnavailableError({
    rateLimited: isRateLimited(error),
  });
}

function withAbortSignal(query: any, signal?: AbortSignal) {
  return signal && typeof query.abortSignal === "function"
    ? query.abortSignal(signal)
    : query;
}

function validateInteger(
  value: unknown,
  { minimum, maximum }: { minimum: number; maximum: number },
): boolean {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= minimum &&
    value <= maximum
  );
}

export function mapRowToEntry(row: any): LeaderboardEntry {
  if (!row || typeof row !== "object") {
    throw new LeaderboardUnavailableError();
  }

  return {
    id: row.id,
    airportCode: row.airport_code || "WEB",
    playerName: row.player_name ?? null,
    // A row written before the score column existed reads as zero rather than
    // NaN, which would sort unpredictably and render as "NaN" on the board.
    score: Number(row.score ?? 0),
    landings: Number(row.landings),
    departures: Number(row.departures),
    pace: Number(row.pace),
    durationSeconds: Number(row.duration_seconds),
    startedAt: row.started_at,
  };
}

export function validateCompletedRun(body: any): ValidatedCompletedRun {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new LeaderboardValidationError();
  }

  const runId = normalizeRunId(body.runId);
  if (
    !validateInteger(body.landings, { minimum: 1, maximum: MAX_SCORE }) ||
    !validateInteger(body.departures, {
      minimum: 0,
      maximum: body.landings,
    })
  ) {
    throw new LeaderboardValidationError();
  }

  if (
    typeof body.durationSeconds !== "number" ||
    !Number.isFinite(body.durationSeconds) ||
    body.durationSeconds <= 0 ||
    body.durationSeconds > MAX_DURATION_SECONDS
  ) {
    throw new LeaderboardValidationError();
  }

  const startedAtDate = new Date(body.startedAt);
  if (
    typeof body.startedAt !== "string" ||
    !Number.isFinite(startedAtDate.getTime())
  ) {
    throw new LeaderboardValidationError();
  }

  const pace = ((body.landings + body.departures) * 60) / body.durationSeconds;
  if (!Number.isFinite(pace) || pace < 0) {
    throw new LeaderboardValidationError();
  }

  // Bounded here as well as in the function, so an impossible score never
  // leaves the tab; the function does not trust this and checks again.
  const flights = body.landings + body.departures;
  const score = body.score ?? 0;
  if (
    !validateInteger(score, {
      minimum: 0,
      maximum: flights * MAX_POINTS_PER_FLIGHT,
    })
  ) {
    throw new LeaderboardValidationError();
  }

  // Recorded, not trusted: it is what a future replay would start from.
  if (
    !validateInteger(body.seed, {
      minimum: 0,
      maximum: Number.MAX_SAFE_INTEGER,
    })
  ) {
    throw new LeaderboardValidationError();
  }

  return {
    runId,
    seed: body.seed,
    score,
    landings: body.landings,
    departures: body.departures,
    durationSeconds: body.durationSeconds,
    startedAt: startedAtDate.toISOString(),
    pace,
  };
}

/**
 * Names that claim to speak for the game.
 *
 * Deliberately about impersonation and nothing else: this is not a profanity
 * filter, which would be a judgement call in every language the board is read
 * in and trivial to write around anyway. Matching is exact against the
 * stripped form, so "Administrator" is refused and "Modest" is not — the
 * `hidden` column is what deals with the ones a list was never going to
 * catch.
 *
 * The edge function keeps its own copy — it cannot import from `src/` — and
 * that copy is the one that decides. Exported so
 * `tests/source/unit/reserved-names.test.ts` can hold the two side by side:
 * drift in either direction is a name the client accepts and the server
 * refuses, or the reverse.
 */
export const RESERVED_PLAYER_NAMES: ReadonlySet<string> = new Set([
  "admin",
  "administrator",
  "moderator",
  "official",
  "staff",
  "system",
  "support",
  "owner",
  "root",
  "pixelflight",
  "pixelflightcontrol",
  "yonetici",
  "sistem",
  "destek",
  "resmi",
]);

/** Lowercase, accents removed, everything but letters dropped. */
function strippedName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z]/gu, "");
}

export function isReservedPlayerName(value: string): boolean {
  return RESERVED_PLAYER_NAMES.has(strippedName(value));
}

export function normalizePlayerName(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    throw new LeaderboardValidationError();
  }

  const normalized = value.trim().replace(/\s+/gu, " ");
  if (normalized.length === 0) {
    return null;
  }
  const capped = Array.from(normalized)
    .slice(0, MAX_PLAYER_NAME_LENGTH)
    .join("");
  // Refused rather than quietly rewritten: a player who typed "Admin" should
  // be told it is taken, not left wondering why the board shows something
  // else.
  if (isReservedPlayerName(capped)) {
    throw new LeaderboardValidationError("That name is reserved.");
  }
  return capped;
}

async function fetchRankedRows(
  client: SupabaseClientLike,
  {
    environment = "production",
    signal,
  }: { environment?: string; signal?: AbortSignal } = {},
): Promise<any[]> {
  let query = client
    .from("leaderboard")
    .select(ENTRY_COLUMNS)
    .eq("environment", normalizeEnvironment(environment))
    // A row taken down for its name is hidden rather than deleted, so the
    // decision stays reversible and the score survives it.
    .eq("hidden", false)
    // Points first: a landing is not worth the same as any other landing, and
    // the counters below are only there to break a tie deterministically.
    .order("score", { ascending: false })
    .order("landings", { ascending: false })
    .order("departures", { ascending: false })
    .order("duration_seconds", { ascending: true })
    .order("started_at", { ascending: true })
    .order("id", { ascending: true })
    .limit(MAX_ENTRIES);
  query = withAbortSignal(query, signal);

  const { data, error } = await query;
  if (error) {
    throwUnavailable(error);
  }
  if (!Array.isArray(data)) {
    throw new LeaderboardUnavailableError();
  }
  return data;
}

export async function listEntries(
  client: SupabaseClientLike,
  {
    environment = "production",
    signal,
  }: { environment?: string; signal?: AbortSignal } = {},
) {
  const rows = await fetchRankedRows(client, { environment, signal });
  return {
    outcome: "listed" as const,
    environment: normalizeEnvironment(environment),
    entries: rows.map(mapRowToEntry),
  };
}
